import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { productsAPI, categoriesAPI } from '../../services/api';
import { Save, ArrowLeft, Upload, X } from 'lucide-react';
import { compressImageFile } from '../../utils/imageCompressor';

const ProductFormMultiImage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category_id: '',
    price: '',
    description: '',
    images: ['', '', '', '', ''],  // 5 images
    stock: '',
    featured: false
  });

  useEffect(() => {
    loadCategories();
    if (isEditMode) {
      loadProduct();
    } else {
      // Pre-fill category if coming from category page
      const categoryParam = searchParams.get('category');
      if (categoryParam) {
        setFormData(prev => ({ ...prev, category_id: categoryParam }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]); // Only re-run when id or isEditMode changes

  const loadCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProduct = async () => {
    try {
      const data = await productsAPI.getById(id);
      
      // Convert old single image_url to images array
      let images = data.images || [];
      if (data.image_url && images.length === 0) {
        images = [data.image_url];
      }
      // Fill remaining slots with empty strings
      while (images.length < 5) {
        images.push('');
      }
      
      setFormData({
        name: data.name,
        slug: data.slug,
        category_id: data.category_id,
        price: data.price,
        description: data.description,
        images: images,
        stock: data.stock,
        featured: data.featured
      });
    } catch (error) {
      console.error('Failed to load product:', error);
      alert('Erreur lors du chargement du produit');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate slug from name
    if (name === 'name' && !isEditMode) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image valide');
      return;
    }

    // No hard size limit — we compress client-side first.
    setUploadingIndex(index);
    try {
      const compressed = await compressImageFile(file);
      const newImages = [...formData.images];
      newImages[index] = compressed;
      setFormData((prev) => ({ ...prev, images: newImages }));
    } catch (err) {
      console.error('Image compression failed:', err);
      alert("Erreur lors du chargement de l'image. Essayez une autre photo.");
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    newImages[index] = '';
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Filter out empty image URLs
      const validImages = formData.images.filter(img => img.trim() !== '');
      
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        images: validImages,
        image_url: validImages[0] || ''  // Set first image as image_url for backward compatibility
      };

      if (isEditMode) {
        await productsAPI.update(id, productData);
      } else {
        await productsAPI.create(productData);
      }

      // If came from categories page, go back to categories
      const categoryParam = searchParams.get('category');
      if (categoryParam && !isEditMode) {
        navigate('/admin/categories');
      } else {
        navigate('/admin/products');
      }
    } catch (error) {
      console.error('Failed to save product:', error);
      alert('Erreur lors de l\'enregistrement du produit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/products')}
            className="p-2 bg-blue-700 text-white rounded-lg hover:bg-blue-600 transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-white">
            {isEditMode ? 'Modifier le produit' : 'Nouveau produit'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="max-w-4xl">
          <div className="bg-blue-800 rounded-lg p-6 border border-blue-700 space-y-6">
            {/* Images Section - 5 photos */}
            <div>
              <label className="block text-white font-semibold mb-3">
                Photos du produit (jusqu'à 5 photos)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {formData.images.map((img, index) => (
                  <div key={`image-slot-${index}`} className="relative">
                    {img ? (
                      <div className="relative">
                        <img
                          src={img}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg border-2 border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <span className="absolute top-2 left-2 bg-cyan-500 text-white px-2 py-1 rounded text-xs font-bold">
                          Photo {index + 1}
                        </span>
                      </div>
                    ) : uploadingIndex === index ? (
                      <div className="border-2 border-dashed border-cyan-500 rounded-lg p-4 h-48 flex flex-col items-center justify-center bg-cyan-50">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-cyan-500 border-t-transparent mb-3"></div>
                        <p className="text-cyan-600 text-sm font-medium">Compression...</p>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-blue-600 rounded-lg p-4 h-48 flex flex-col items-center justify-center">
                        <Upload className="w-8 h-8 text-blue-400 mb-2" />
                        <p className="text-gray-400 text-sm mb-2">Photo {index + 1}</p>
                        
                        {/* File upload button */}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(index, e.target.files[0])}
                          className="hidden"
                          id={`file-upload-${index}`}
                        />
                        <label
                          htmlFor={`file-upload-${index}`}
                          className="mb-2 px-3 py-1 bg-cyan-500 text-white rounded text-sm cursor-pointer hover:bg-cyan-600"
                        >
                          Choisir un fichier
                        </label>
                        
                        {/* OR URL input */}
                        <div className="text-gray-500 text-xs mb-1">ou</div>
                        <input
                          type="url"
                          value={img}
                          onChange={(e) => handleImageChange(index, e.target.value)}
                          placeholder="URL de l'image"
                          className="w-full bg-blue-700 border border-blue-600 rounded px-2 py-1 text-white text-sm"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-2">
                💡 Astuce : La première photo sera l'image principale
              </p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-white font-semibold mb-2">Nom du produit *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-blue-700 border border-blue-600 rounded-lg px-4 py-3 text-white"
                placeholder="Ex: Manette PS5 DualSense"
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-white font-semibold mb-2">Slug (URL) *</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                className="w-full bg-blue-700 border border-blue-600 rounded-lg px-4 py-3 text-white font-mono"
                placeholder="manette-ps5-dualsense"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-white font-semibold mb-2">Catégorie *</label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
                className="w-full bg-blue-700 border border-blue-600 rounded-lg px-4 py-3 text-white"
              >
                <option value="">Sélectionnez une catégorie</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Price & Stock */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-semibold mb-2">Prix (DA) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full bg-blue-700 border border-blue-600 rounded-lg px-4 py-3 text-white"
                  placeholder="12500"
                />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2">Stock *</label>
                <input
                  type="number"
                  name="stock"
                  value={formData.stock}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full bg-blue-700 border border-blue-600 rounded-lg px-4 py-3 text-white"
                  placeholder="10"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-white font-semibold mb-2">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                className="w-full bg-blue-700 border border-blue-600 rounded-lg px-4 py-3 text-white"
                placeholder="Description détaillée du produit..."
              />
            </div>

            {/* Featured */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="featured"
                id="featured"
                checked={formData.featured}
                onChange={handleChange}
                className="w-5 h-5 text-cyan-400"
              />
              <label htmlFor="featured" className="text-white font-semibold">
                ⭐ Produit vedette (afficher sur la page d'accueil)
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-cyan-500 text-white font-semibold py-3 rounded-lg hover:bg-cyan-600 transition disabled:bg-blue-600 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/products')}
                className="px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-600 transition"
              >
                Annuler
              </button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ProductFormMultiImage;
